import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';

const QuickLogin = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { quickLogin, logout } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  // Run the magic-link login EXACTLY once. Without this, the effect could re-run
  // (e.g. if an auth-context callback identity changes or under StrictMode),
  // firing multiple logins + multiple navigations and leaving the app in a stuck
  // blank state that only a manual refresh recovers from.
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const performQuickLogin = async () => {
      if (!token) {
        setStatus('error');
        setMessage('No login token provided');
        return;
      }

      try {
        setStatus('loading');
        setMessage('Authenticating...');
        
        const result = await quickLogin(token);
        
        if (result.success) {
          setStatus('success');
          setMessage('Login successful! Redirecting...');
          
          // Short delay to show success message
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1000);
        } else {
          if ((result.error || '').includes('Invalid or expired quick login link')) {
            navigate('/link-revoked', { replace: true });
            return;
          }
          setStatus('error');
          setMessage(result.error || 'Login failed');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.message || 'An unexpected error occurred');
      }
    };

    performQuickLogin();
  }, [token, quickLogin, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Quick Login</h2>
              <p className="text-slate-300">{message}</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Success!</h2>
              <p className="text-slate-300">{message}</p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Login Failed</h2>
              <p className="text-slate-300 mb-4">{message}</p>
              <button
                onClick={() => {
                  // Clear any existing session so it doesn't auto-login
                  logout();
                  navigate('/login', { replace: true });
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Login Page
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickLogin;
